import { createSelector } from '@reduxjs/toolkit';

import {
  RelationshipWithHierarchyData,
  getAllInheritedRelationships,
} from 'src/containers/ProjectDiagram/utils/getAllInheritedRelationships';
import { memoizeSelectorFactory } from 'src/selectors/memoizeSelectorFactory';
import { projectClassHierarchySelector } from 'src/selectors/projectClassHierarchy';
import { projectClassIrisSelector } from 'src/selectors/projectClassIris';
import { projectLabelsByIriSelector } from 'src/selectors/projectLabelsByIri';
import { projectLinkDataArraySelector } from 'src/selectors/projectLinkDataArray';

export const projectClassRelationshipsSelector = memoizeSelectorFactory(
  (projectId: string, classIri: string) =>
    createSelector(
      projectClassHierarchySelector(projectId),
      projectLinkDataArraySelector(projectId),
      projectLabelsByIriSelector(projectId),
      (hierarchyByIri, linkDataArray, labelsByIri) => {
        if (!classIri) {
          return {};
        }

        return getAllInheritedRelationships({
          classIri,
          hierarchyByIri,
          labelsByIri,
          linkDataArray,
        });
      }
    )
);

export const projectClassRelationshipsByClassSelector = memoizeSelectorFactory(
  (projectId: string) =>
    createSelector(
      projectClassIrisSelector(projectId),
      projectClassHierarchySelector(projectId),
      projectLinkDataArraySelector(projectId),
      projectLabelsByIriSelector(projectId),
      (classIris, hierarchyByIri, linkDataArray, labelsByIri) => {
        return Array.from(classIris).reduce<
          Record<string, Record<string, RelationshipWithHierarchyData>>
        >((acc, classIri) => {
          const relationshipsByClass = getAllInheritedRelationships({
            classIri,
            hierarchyByIri,
            labelsByIri,
            linkDataArray,
          });
          acc[classIri] = relationshipsByClass;
          return acc;
        }, {});
      }
    )
);
